<template>
  <div v-if="shouldShow" class="measurement-step">
    <div class="measurement-content">
      <!-- Title -->
      <div class="title-container">
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Hint Button -->
      <div v-if="step.hint" class="hint-section">
        <button @click="openHint" class="hint-button">
          <CustomSvgIcons name="scope" :size="16" />
          {{ step.hint.title }}
          <i class="pi pi-arrow-right"></i>
        </button>
      </div>
    </div>

      <!-- Shoe Size System Toggle (only for shoeSize) -->
      <div v-if="isShoeSizeStep" class="shoe-size-toggle-section">
        <button
          v-for="system in shoeSizeSystems"
          :key="system"
          @click="selectShoeSizeSystem(system)"
          :class="['shoe-size-toggle-button', { active: selectedShoeSizeSystem === system }]"
        >
          {{ system }}
        </button>
      </div>

      <!-- Measurement Picker -->
      <div class="picker-section">
        <div class="picker-container">
          <CustomDrumPicker
            v-model="selectedValue"
            :items="pickerOptions"
            :orientation="effectiveOrientation"
            :unit="isShoeSizeStep ? ' ' : effectiveUnit"
            :class="['measurement-picker', effectiveOrientation]"
          />
        </div>
      </div>
    </div>

    <!-- Hint Drawer (mobile/tablet) -->
    <CustomDrawer
      v-if="step.hint && !isDesktop"
      v-model="showHint"
      :title="step.hint.title"
      :video-src="step.hint.video_src || undefined"
      :description="step.hint.description"
    />

    <!-- Hint Dialog (desktop) -->
    <Dialog
      v-if="step.hint && isDesktop"
      v-model:visible="showHint"
      modal
      :closable="true"
      :style="{
        width: 'fit-content',
        maxWidth: '90vw',
        borderRadius: '20px',
      }"
      :pt="{
        root: {
          style: {
            background: '#FFFFFF',
          },
        },
      }"
    >
      <template #header>
        <div class="hint-dialog-header">
        </div>
      </template>

      <div class="hint-dialog-content">
        <div class="drawer-measure-image">
              <img :src="measureImage" :alt="'measure'" />
        </div>
        <h3 class="hint-dialog-title">{{ step.hint.title }}</h3>

        <div v-if="step.hint.description" class="hint-dialog-description">
          <p>{{ step.hint.description }}</p>
        </div>
      </div>

      <template #footer>
        <div class="hint-dialog-footer">
          <Button @click="showHint = false" label="Понятно" class="hint-dialog-button" rounded />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import CustomDrumPicker from '@/components/UI/CustomDrumPicker.vue'
import CustomDrawer from '@/components/UI/CustomDrawer.vue'
import CustomSvgIcons from '@/components/HelpersComponents/CustomSvgIcons.vue'
import { useSurveyModal } from '@/features/survey/store'
import type { SurveyStep } from '@/features/survey/types/survey-config'
import {
  type ShoeSizeSystem,
  convertShoeSize,
  generateShoeSizes,
  getShoeSizeRange
} from '@/utils/shoeSizeConverter'

interface Props {
  step: SurveyStep & ({ type: 'measurement' } | { type: 'shoe_size' })
}

const props = defineProps<Props>()
const surveyStore = useSurveyModal()
const selectedValue = ref<number | null>(null)
const showHint = ref(false)

// Shoe size system state
const isShoeSizeStep = computed(() => props.step.type === 'shoe_size')
const shoeSizeSystems: ShoeSizeSystem[] = ['EUR', 'UK', 'US']
const selectedShoeSizeSystem = ref<ShoeSizeSystem>('EUR')

const measureImage = ref("img/survey/height_img/height.png");
// Определяем размер экрана: >= 1024px = desktop
const isDesktop = useMediaQuery('(min-width: 1024px)')

// Используем shouldStepBeVisible из store для правильной обработки условной логики
// (поддержка как старого формата field/value, так и нового формата с rules)
const shouldShow = computed(() => {
  return surveyStore.shouldStepBeVisible(props.step)
})

// Дефолтные значения для shoe_size
const defaultShoeSizeUnit = 'размер'
const defaultShoeSizeOrientation = 'horizontal' as const

// Computed для получения эффективных значений с дефолтами для shoe_size
const effectiveRange = computed(() => {
  if (props.step.type === 'shoe_size') {
    // Для shoeSize используем диапазон из выбранной системы
    return getShoeSizeRange(selectedShoeSizeSystem.value)
  }
  return props.step.range
})

const effectiveOrientation = computed(() => {
  if (props.step.type === 'shoe_size') {
    return props.step.orientation || defaultShoeSizeOrientation
  }
  return props.step.orientation
})

const effectiveUnit = computed(() => {
  if (props.step.type === 'shoe_size') {
    return props.step.unit || defaultShoeSizeUnit
  }
  return props.step.unit
})

// Мемоизируем pickerOptions, чтобы не пересчитывать при каждом рендере
const pickerOptions = computed(() => {
  // Для shoeSize используем размеры из выбранной системы
  if (isShoeSizeStep.value) {
    return generateShoeSizes(selectedShoeSizeSystem.value)
  }

  // Для обычных измерений используем стандартную логику
  const { min, max, step: stepValue = 1 } = effectiveRange.value
  const count = Math.floor((max - min) / stepValue) + 1
  // Используем предварительно созданный массив для лучшей производительности
  const options = new Array(count)
  for (let i = 0; i < count; i++) {
    const val = min + i * stepValue
    options[i] = {
      value: val,
      label: String(val)
    }
  }
  return options
})

let updateTimeout: ReturnType<typeof setTimeout> | null = null

function updateValue(value: number | null) {
  // Debounce обновление данных, чтобы избежать лишних пересчетов при прокрутке picker
  if (updateTimeout) {
    clearTimeout(updateTimeout)
  }
  updateTimeout = setTimeout(() => {
    // Используем step.id как ключ, так как бэкенд возвращает measurement_type (snake_case)
    // и оно может быть undefined. step.id всегда есть и соответствует ключу в answers
    surveyStore.updateSurveyData(props.step.id, value)
    
    // Для shoeSize также сохраняем выбранную систему
    if (isShoeSizeStep.value) {
      surveyStore.updateSurveyData(`${props.step.id}_system`, selectedShoeSizeSystem.value)
    }
    
    updateTimeout = null
  }, 150) // Обновляем через 150ms после последнего изменения
}

function openHint() {
  showHint.value = true
}

function selectShoeSizeSystem(system: ShoeSizeSystem) {
  if (selectedShoeSizeSystem.value === system) return

  const oldSystem = selectedShoeSizeSystem.value
  selectedShoeSizeSystem.value = system

  // Конвертируем текущее значение в новую систему
  if (selectedValue.value !== null) {
    const convertedValue = convertShoeSize(selectedValue.value, oldSystem, system)
    if (convertedValue !== null) {
      // Находим ближайшее доступное значение в новой системе
      const availableSizes = generateShoeSizes(system)
      const closestSize = availableSizes.reduce((prev, curr) => {
        const prevDiff = Math.abs(prev.value - convertedValue)
        const currDiff = Math.abs(curr.value - convertedValue)
        return currDiff < prevDiff ? curr : prev
      })
      selectedValue.value = closestSize.value
    } else {
      // Если конвертация не удалась, устанавливаем первое доступное значение
      const firstSize = generateShoeSizes(system)[0]
      if (firstSize) {
        selectedValue.value = firstSize.value
      }
    }
  }

  // Сохраняем выбранную систему в store
  surveyStore.updateSurveyData(`${props.step.id}_system`, system)
}

watch(selectedValue, updateValue)

function initValueFromStep() {
  if (isShoeSizeStep.value) {
    const savedSystem = surveyStore.surveyModal.surveyData[`${props.step.id}_system`] as ShoeSizeSystem | undefined
    if (savedSystem && shoeSizeSystems.includes(savedSystem)) {
      selectedShoeSizeSystem.value = savedSystem
    }

    const savedValue = surveyStore.surveyModal.surveyData[props.step.id]
    if (savedValue !== null && savedValue !== undefined) {
      selectedValue.value = savedValue as number
    } else {
      const firstSize = generateShoeSizes(selectedShoeSizeSystem.value)[0]
      if (firstSize) {
        selectedValue.value = firstSize.value
      }
    }
  } else {
    selectedValue.value = surveyStore.surveyModal.surveyData[props.step.id] ?? null
  }
}

onMounted(initValueFromStep)

// При смене шага (тот же компонент переиспользуется для другого step) сбрасываем значение на данные текущего шага
watch(
  () => props.step.id,
  () => {
    initValueFromStep()
  }
)
</script>

<style scoped>

.drawer-measure-image {
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
}

.drawer-measure-image img {
  height: 340px;
  align-self: stretch;
  aspect-ratio: 361/340;
}



.measurement-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  align-self: stretch;
}

.measurement-content {
  display: flex;
  padding: var(--Padding-XXL, 40px) 120px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--Spacing-XL, 40px);
  align-self: stretch;
}

.title-section {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
}

.title-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--Spacing-S, 8px);
  align-self: stretch;
}

.step-title {
  font-size: 48px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0;
}

.step-subtitle {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.hint-section {
  display: flex;
}

.hint-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--yellow-80);
  border: none;
  border-radius: 28px;
  color: var(--active-100);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.hint-button:hover {
  background: #e5e5e7;
}

.hint-button i {
  font-size: 16px;
}

.shoe-size-toggle-section {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.shoe-size-toggle-button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid #E6E3EC;
  background: #FFFFFF;
  color: #6E85CB;
  font-family: Gilroy;
  font-size: 19px;
  font-weight: 500;
  line-height: 1.09;
  letter-spacing: -0.05em;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 50px;
}

.shoe-size-toggle-button:hover {
  background: #f5f5f7;
}

.shoe-size-toggle-button.active {
  background: #6E85CB;
  color: #FFFFFF;
  border-color: #6E85CB;
}

.picker-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.picker-container {
  position: relative;
}

.measurement-picker.vertical {
  width: 120px;
}

.measurement-picker.horizontal {
  height: 110px;
  width: 354px;
}

.measurement-unit {
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;
}

.measurement-picker.vertical + .measurement-unit {
  margin-top: 75px;
}

@media (max-width: 1440px) {
  .measurement-content {
    padding: 0;
  }
}

@media (max-width: 1024px) {

.measurement-step {
  padding: 60px 20px 20px 20px;
}
  
  .measurement-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-XL, 40px);
  padding: 0;
    align-self: stretch; 
  }

  .title-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--Spacing-S, 8px);
    align-self: stretch;
  }


  .title-section {

    text-align: center;
  }

  .step-title {
    align-self: stretch;
    color: var(--Color-Primary-blue-100, #232F45);
    font-variant-numeric: ordinal;
    font-feature-settings: 'case' on, 'liga' off;

    /* FAIR Typography/App/H1-bold */
    font-family: Gilroy;
    font-size: 32px;
    font-style: normal;
    font-weight: 700;
    line-height: 109%; /* 34.88px */
    letter-spacing: -0.64px;
  }
}

/* Hint Dialog Styles (desktop) */
.hint-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  margin-bottom: 20px;
}

.hint-dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0;
}

.hint-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 400px;
  max-width: 600px;
}

.hint-dialog-video {
  width: 100%;
  margin-bottom: 16px;
}

.hint-dialog-video-player {
  width: 100%;
  border-radius: 12px;
  background: #000;
}

.hint-dialog-description {
  color: #666;
  line-height: 1.5;
}

.hint-dialog-description p {
  margin: 0;
}

.hint-dialog-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 20px;
  margin-top: 20px;
  width:100%;
}

.hint-dialog-button {
  min-width: 120px;
  width:100%;
}
</style>

